import React, { useState, useEffect, useCallback } from 'react'
import { useNotificationActions } from '@/contexts/NotificationContext'
import { useSettingsStore } from '@/store/settingsStore'
import { apiService } from '@/services/api'
import { BillPaymentReceipt } from '@/components/pos/BillPaymentReceipt'
import { BILL_TYPES, PAYMENT_METHODS_BILL, type BillTypeOption } from '@/constants/billTypes'
import type { Transaction } from '@/types/pos'
import { ArrowLeft, Receipt, CreditCard, Banknote, Smartphone, Search, History, PlusCircle, FileText } from 'lucide-react'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'

export const BillPaymentsPage: React.FC = () => {
  const { settings } = useSettingsStore()
  const { showSuccess, showError } = useNotificationActions()

  // Tab state
  const [activeTab, setActiveTab] = useState<'new' | 'history'>('new')

  // Bill types from backend
  const [billTypesList, setBillTypesList] = useState<BillTypeOption[]>([])
  const [loadingBillTypes, setLoadingBillTypes] = useState(true)
  const [selectedBillType, setSelectedBillType] = useState<BillTypeOption | null>(null)

  useEffect(() => {
    setLoadingBillTypes(true)
    apiService
      .get<{ success: boolean; data: BillTypeOption[] }>('/bill-categories')
      .then((res: any) => {
        if (res?.success && Array.isArray(res.data) && res.data.length > 0) {
          setBillTypesList(res.data)
        } else {
          setBillTypesList(BILL_TYPES)
        }
      })
      .catch(() => {
        setBillTypesList(BILL_TYPES)
      })
      .finally(() => {
        setLoadingBillTypes(false)
      })
  }, [])

  // Form state
  const [provider, setProvider] = useState('')
  const [accountRef, setAccountRef] = useState('')
  const [amount, setAmount] = useState('')
  const [serviceFee, setServiceFee] = useState('')
  const [customerName, setCustomerName] = useState('')
  const [reference, setReference] = useState('')
  const [paymentMethod, setPaymentMethod] = useState<string>('cash')
  const [notes, setNotes] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [lastTransaction, setLastTransaction] = useState<Transaction | null>(null)
  const [showReceipt, setShowReceipt] = useState(false)

  // Advance payment integration
  const [advanceSearch, setAdvanceSearch] = useState('')
  const [advanceLookupResults, setAdvanceLookupResults] = useState<any[]>([])
  const [advanceLookupLoading, setAdvanceLookupLoading] = useState(false)
  const [appliedAdvance, setAppliedAdvance] = useState<{ id: string; requestId: string; amount: number } | null>(null)

  // History state
  const [historyTxns, setHistoryTxns] = useState<Transaction[]>([])
  const [historyLoading, setHistoryLoading] = useState(false)
  const [historySearch, setHistorySearch] = useState('')

  // Auto-fill service fee when bill type selected
  useEffect(() => {
    if (selectedBillType) {
      setServiceFee(String(selectedBillType.serviceFee ?? 0))
    }
  }, [selectedBillType])

  // Fetch today's bill payment history
  const fetchHistory = useCallback(async () => {
    setHistoryLoading(true)
    try {
      const today = new Date().toISOString().slice(0, 10)
      const res = await apiService.get<{ success: boolean; data: Transaction[] }>(`/transactions?type=bill_payment&from=${today}&to=${today}`)
      if (res.success && Array.isArray(res.data)) {
        setHistoryTxns(res.data)
      }
    } catch {
      // Fallback: try fetching all transactions and filter client-side
      try {
        const res = await apiService.get<{ success: boolean; data: Transaction[] }>('/transactions')
        if (res.success && Array.isArray(res.data)) {
          const today = new Date().toISOString().slice(0, 10)
          const billTxns = res.data.filter((t: any) =>
            t.type === 'bill_payment' && t.createdAt?.startsWith(today)
          )
          setHistoryTxns(billTxns)
        }
      } catch {
        setHistoryTxns([])
      }
    } finally {
      setHistoryLoading(false)
    }
  }, [])

  useEffect(() => {
    if (activeTab === 'history') {
      fetchHistory()
    }
  }, [activeTab, fetchHistory])

  // Advance lookup
  const handleAdvanceLookup = async () => {
    const q = advanceSearch.trim()
    if (!q) return
    setAdvanceLookupLoading(true)
    try {
      const res = await apiService.get<{ success: boolean; data: any[] }>(`/advance-payments/lookup?q=${encodeURIComponent(q)}`)
      if (res.success && Array.isArray(res.data)) {
        setAdvanceLookupResults(res.data)
      } else {
        setAdvanceLookupResults([])
      }
    } catch {
      setAdvanceLookupResults([])
    } finally {
      setAdvanceLookupLoading(false)
    }
  }

  const applyAdvance = (adv: any) => {
    const remaining = (adv.amount || 0) - (adv.appliedAmount || 0)
    setAppliedAdvance({ id: adv.id, requestId: adv.requestId, amount: remaining })
    setAdvanceLookupResults([])
    setAdvanceSearch('')
  }

  const numAmount = parseFloat(amount) || 0
  const numServiceFee = parseFloat(serviceFee) || 0
  const totalDue = numAmount + numServiceFee
  const advanceAppliedAmount = appliedAdvance ? Math.min(appliedAdvance.amount, totalDue) : 0
  const balanceDue = totalDue - advanceAppliedAmount

  const resetForm = () => {
    setSelectedBillType(null)
    setProvider('')
    setAccountRef('')
    setAmount('')
    setServiceFee('')
    setCustomerName('')
    setReference('')
    setPaymentMethod('cash')
    setNotes('')
    setAppliedAdvance(null)
    setAdvanceSearch('')
    setAdvanceLookupResults([])
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedBillType || !Number.isFinite(numAmount) || numAmount <= 0) {
      showError('Invalid', 'Select a bill type and enter a valid amount.')
      return
    }
    setIsSubmitting(true)
    try {
      const res = await apiService.post<{ success: boolean; data: Transaction }>('/transactions/bill-payment', {
        billType: selectedBillType.label,
        provider: provider.trim() || undefined,
        accountRef: accountRef.trim() || undefined,
        amount: numAmount,
        serviceFee: numServiceFee,
        customerName: customerName.trim() || undefined,
        reference: reference.trim() || undefined,
        paymentMethod,
        notes: notes.trim() || undefined,
        advanceApplied: advanceAppliedAmount > 0 ? advanceAppliedAmount : undefined,
      })
      if (res.success && res.data) {
        // Apply advance on backend if used
        if (appliedAdvance && advanceAppliedAmount > 0) {
          try {
            await apiService.patch(`/advance-payments/${appliedAdvance.id}/apply`, {
              applyAmount: advanceAppliedAmount,
              transactionId: res.data.id || (res.data as any).transactionNumber,
            })
          } catch (err) {
            console.warn('Failed to apply advance on backend:', err)
            showError('Warning', 'Payment recorded but advance deduction failed. Please check manually.')
          }
        }
        setLastTransaction(res.data)
        setShowReceipt(true)
        showSuccess('Payment Complete', `Rs.${totalDue.toFixed(2)} ${selectedBillType.label} payment recorded.`)
      } else {
        showError('Failed', 'Could not complete payment.')
      }
    } catch (err: any) {
      showError('Error', err?.response?.data?.message || err?.message || 'Payment failed.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const paymentIcon = (id: string) => {
    switch (id) {
      case 'cash': return <Banknote className="w-5 h-5" />
      case 'card': return <CreditCard className="w-5 h-5" />
      case 'digital_wallet': return <Smartphone className="w-5 h-5" />
      case 'bank_transfer': return <Receipt className="w-5 h-5" />
      default: return <Banknote className="w-5 h-5" />
    }
  }

  const filteredHistory = historySearch
    ? historyTxns.filter(t =>
      ((t as any).transactionNumber || '').toLowerCase().includes(historySearch.toLowerCase()) ||
      ((t as any).billType || '').toLowerCase().includes(historySearch.toLowerCase()) ||
      ((t as any).customerName || '').toLowerCase().includes(historySearch.toLowerCase())
    )
    : historyTxns

  return (
    <div className="min-h-screen bg-gradient-space p-3 sm:p-4 lg:p-6">
      <div className="w-full max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold esales-text font-display">Bill Payments</h1>
          <p className="text-muted-foreground mt-2">Process mobile reloads, utility bills, and other payments.</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            type="button"
            onClick={() => setActiveTab('new')}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm transition-all ${
              activeTab === 'new'
                ? 'bg-primary text-primary-foreground shadow-lg'
                : 'bg-muted hover:bg-muted/80 text-muted-foreground'
            }`}
          >
            <PlusCircle className="w-4 h-4" />
            New Payment
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('history')}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm transition-all ${
              activeTab === 'history'
                ? 'bg-primary text-primary-foreground shadow-lg'
                : 'bg-muted hover:bg-muted/80 text-muted-foreground'
            }`}
          >
            <History className="w-4 h-4" />
            Today's History
          </button>
        </div>

        {activeTab === 'new' && (
          <>
            {!selectedBillType ? (
              /* Bill type grid */
              <div className="glass-card p-6">
                <h2 className="text-lg font-semibold mb-4">Select bill type</h2>
                {loadingBillTypes ? (
                  <div className="flex justify-center py-12">
                    <LoadingSpinner size="md" message="Loading categories..." />
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                    {billTypesList.map((bill) => (
                      <button
                        key={bill.id}
                        type="button"
                        onClick={() => setSelectedBillType(bill)}
                        className="flex flex-col items-center justify-center p-6 rounded-xl border-2 border-border bg-background/50 hover:border-primary hover:bg-primary/5 transition-all hover:shadow-lg"
                      >
                        <span className="text-3xl mb-2">{bill.icon || '📄'}</span>
                        <span className="font-semibold text-sm text-center">{bill.label}</span>
                        {bill.description && (
                          <span className="text-xs text-muted-foreground mt-1 text-center">{bill.description}</span>
                        )}
                        {(bill.serviceFee ?? 0) > 0 && (
                          <span className="text-xs text-primary font-bold mt-1">Fee: Rs.{bill.serviceFee}</span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              /* Payment form */
              <div className="glass-card p-6">
                <button
                  type="button"
                  onClick={resetForm}
                  className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span className="text-sm font-medium">Change bill type</span>
                </button>
                <div className="flex items-center gap-3 mb-6 pb-4 border-b border-border">
                  <span className="text-3xl">{selectedBillType.icon}</span>
                  <div>
                    <h2 className="text-xl font-bold">{selectedBillType.label}</h2>
                    <p className="text-sm text-muted-foreground">{selectedBillType.description}</p>
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-xs font-bold text-muted-foreground uppercase tracking-wider mb-1">Provider / Operator (optional)</label>
                      <input
                        type="text"
                        value={provider}
                        onChange={(e) => setProvider(e.target.value)}
                        placeholder="e.g. Mobitel, SLT, CEB"
                        className="w-full px-4 py-3 bg-background/50 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-muted-foreground uppercase tracking-wider mb-1">Account / Phone / Reference</label>
                      <input
                        type="text"
                        value={accountRef}
                        onChange={(e) => setAccountRef(e.target.value)}
                        placeholder="Account number or phone"
                        className="w-full px-4 py-3 bg-background/50 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-muted-foreground uppercase tracking-wider mb-1">Bill Amount (Rs.) *</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        required
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        placeholder="0.00"
                        className="w-full px-4 py-3 bg-background/50 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:outline-none text-lg font-semibold"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-muted-foreground uppercase tracking-wider mb-1">Service Fee (Rs.)</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={serviceFee}
                        onChange={(e) => setServiceFee(e.target.value)}
                        placeholder="0.00"
                        className="w-full px-4 py-3 bg-background/50 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:outline-none text-lg font-semibold"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-muted-foreground uppercase tracking-wider mb-1">Customer name (optional)</label>
                      <input
                        type="text"
                        value={customerName}
                        onChange={(e) => setCustomerName(e.target.value)}
                        placeholder="Customer name"
                        className="w-full px-4 py-3 bg-background/50 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-muted-foreground uppercase tracking-wider mb-1">Reference / Receipt no. (optional)</label>
                      <input
                        type="text"
                        value={reference}
                        onChange={(e) => setReference(e.target.value)}
                        placeholder="External reference"
                        className="w-full px-4 py-3 bg-background/50 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:outline-none"
                      />
                    </div>
                  </div>

                  {/* Total breakdown */}
                  <div className="bg-muted/50 rounded-xl p-4 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Bill Amount:</span>
                      <span>Rs.{numAmount.toFixed(2)}</span>
                    </div>
                    {numServiceFee > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Service Fee:</span>
                        <span>Rs.{numServiceFee.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between font-bold text-lg border-t border-border pt-2">
                      <span>Total:</span>
                      <span>Rs.{totalDue.toFixed(2)}</span>
                    </div>
                    {advanceAppliedAmount > 0 && (
                      <>
                        <div className="flex justify-between text-sm text-green-500">
                          <span>Advance Applied:</span>
                          <span>-Rs.{advanceAppliedAmount.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between font-bold text-base border-t border-border pt-2">
                          <span>Balance Due:</span>
                          <span>Rs.{balanceDue.toFixed(2)}</span>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Advance payment lookup */}
                  <div className="border border-border rounded-xl p-4">
                    <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2">Apply Advance Payment (optional)</p>
                    {appliedAdvance ? (
                      <div className="flex items-center justify-between bg-green-500/10 border border-green-500/30 rounded-lg p-3">
                        <div>
                          <p className="text-sm font-semibold text-green-600">{appliedAdvance.requestId}</p>
                          <p className="text-xs text-muted-foreground">Applied: Rs.{advanceAppliedAmount.toFixed(2)}</p>
                        </div>
                        <button type="button" onClick={() => setAppliedAdvance(null)} className="text-xs text-red-500 hover:text-red-600 font-semibold">Remove</button>
                      </div>
                    ) : (
                      <div>
                        <div className="flex gap-2 mb-2">
                          <input
                            type="text"
                            value={advanceSearch}
                            onChange={(e) => setAdvanceSearch(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAdvanceLookup() } }}
                            placeholder="Receipt ID or phone number"
                            className="flex-1 px-3 py-2 bg-background/50 border border-border rounded-lg text-sm focus:ring-2 focus:ring-primary focus:outline-none"
                          />
                          <button type="button" onClick={handleAdvanceLookup} disabled={advanceLookupLoading} className="px-4 py-2 bg-primary/10 hover:bg-primary/20 text-primary rounded-lg text-sm font-semibold transition-colors">
                            {advanceLookupLoading ? '...' : <Search className="w-4 h-4" />}
                          </button>
                        </div>
                        {advanceLookupResults.length > 0 && (
                          <div className="space-y-2 max-h-40 overflow-y-auto">
                            {advanceLookupResults.map((adv: any) => {
                              const remaining = (adv.amount || 0) - (adv.appliedAmount || 0)
                              return (
                                <button
                                  key={adv.id}
                                  type="button"
                                  onClick={() => applyAdvance(adv)}
                                  className="w-full flex items-center justify-between p-3 bg-background/50 border border-border rounded-lg hover:border-primary/50 transition-colors text-left"
                                >
                                  <div>
                                    <p className="text-sm font-semibold">{adv.requestId}</p>
                                    <p className="text-xs text-muted-foreground">
                                      {adv.customerName || adv.customerPhone || 'Guest'} - {new Date(adv.createdAt).toLocaleDateString()}
                                    </p>
                                  </div>
                                  <span className="text-sm font-bold text-green-500">Rs.{remaining.toFixed(2)}</span>
                                </button>
                              )
                            })}
                          </div>
                        )}
                        {advanceLookupResults.length === 0 && advanceSearch && !advanceLookupLoading && (
                          <p className="text-xs text-muted-foreground">No active advances found.</p>
                        )}
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2">Payment method</label>
                    <div className="flex flex-wrap gap-3">
                      {PAYMENT_METHODS_BILL.map((pm) => (
                        <button
                          key={pm.id}
                          type="button"
                          onClick={() => setPaymentMethod(pm.id)}
                          className={`flex items-center gap-2 px-4 py-3 rounded-xl border-2 transition-all ${
                            paymentMethod === pm.id
                              ? 'border-primary bg-primary/10 text-primary'
                              : 'border-border bg-background/50 hover:border-primary/50'
                          }`}
                        >
                          {paymentIcon(pm.id)}
                          <span className="font-medium text-sm">{pm.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-muted-foreground uppercase tracking-wider mb-1">Notes (optional)</label>
                    <input
                      type="text"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Internal notes"
                      className="w-full px-4 py-3 bg-background/50 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:outline-none"
                    />
                  </div>

                  <div className="flex gap-3 pt-4">
                    <button
                      type="button"
                      onClick={resetForm}
                      className="flex-1 py-3.5 bg-muted hover:bg-muted/80 rounded-xl font-semibold transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting || !amount || numAmount <= 0}
                      className="flex-1 py-3.5 bg-primary text-primary-foreground rounded-xl font-semibold glow-effect hover:opacity-95 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    >
                      {isSubmitting ? 'Processing...' : `Pay Rs.${totalDue.toFixed(2)}`}
                    </button>
                  </div>
                </form>
              </div>
            )}
          </>
        )}

        {activeTab === 'history' && (
          <div className="glass-card p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Today's Bill Payments</h2>
              <button type="button" onClick={fetchHistory} className="text-xs text-primary hover:text-primary/80 font-semibold">Refresh</button>
            </div>

            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                type="text"
                value={historySearch}
                onChange={(e) => setHistorySearch(e.target.value)}
                placeholder="Search by invoice #, type, or customer..."
                className="w-full pl-10 pr-4 py-2.5 bg-background/50 border border-border rounded-lg text-sm focus:ring-2 focus:ring-primary focus:outline-none"
              />
            </div>

            {historyLoading ? (
              <div className="flex justify-center py-12">
                <LoadingSpinner size="md" message="Loading history..." />
              </div>
            ) : filteredHistory.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>No bill payments found for today.</p>
              </div>
            ) : (
              <>
                {/* Mobile card view */}
                <div className="md:hidden space-y-3">
                  {filteredHistory.map((t: any) => {
                    const billAmt = Number(t.subtotal) || Math.max(0, (Number(t.total) || 0) - (Number(t.serviceFee) || 0))
                    const feeAmt = Number(t.serviceFee) || 0
                    const totalAmt = Number(t.total) || 0
                    return (
                      <div key={t.id} className="bg-background/50 border border-border rounded-xl p-4 space-y-3">
                        {/* Card header: invoice + status */}
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <p className="font-mono text-xs text-muted-foreground">{t.transactionNumber || t.receiptNumber || '-'}</p>
                            <p className="font-semibold text-sm mt-0.5">{t.billType || '-'}</p>
                          </div>
                          {t.status && (
                            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full shrink-0 ${
                              t.status === 'completed' ? 'bg-green-500/15 text-green-500' :
                              t.status === 'refunded' ? 'bg-red-500/15 text-red-500' :
                              'bg-muted text-muted-foreground'
                            }`}>
                              {t.status}
                            </span>
                          )}
                        </div>

                        {/* Provider + account ref */}
                        {(t.provider || t.accountRef) && (
                          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                            {t.provider && <span>Provider: <span className="text-foreground font-medium">{t.provider}</span></span>}
                            {t.accountRef && <span>Ref: <span className="text-foreground font-medium">{t.accountRef}</span></span>}
                          </div>
                        )}

                        {/* Amounts row */}
                        <div className="grid grid-cols-3 gap-2 text-xs">
                          <div className="bg-muted/40 rounded-lg p-2 text-center">
                            <p className="text-muted-foreground mb-0.5">Amount</p>
                            <p className="font-semibold">Rs.{billAmt.toFixed(2)}</p>
                          </div>
                          <div className="bg-muted/40 rounded-lg p-2 text-center">
                            <p className="text-muted-foreground mb-0.5">Fee</p>
                            <p className="font-semibold text-primary">{feeAmt > 0 ? `Rs.${feeAmt.toFixed(2)}` : '-'}</p>
                          </div>
                          <div className="bg-primary/10 rounded-lg p-2 text-center">
                            <p className="text-muted-foreground mb-0.5">Total</p>
                            <p className="font-bold text-primary">Rs.{totalAmt.toFixed(2)}</p>
                          </div>
                        </div>

                        {/* Footer: payment method + time */}
                        <div className="flex items-center justify-between text-xs text-muted-foreground pt-1 border-t border-border/50">
                          <span className="capitalize font-medium">{(t.paymentMethod || 'cash').replace(/_/g, ' ')}</span>
                          <span>{new Date(t.createdAt).toLocaleTimeString()}</span>
                        </div>
                      </div>
                    )
                  })}

                  {/* Mobile totals summary */}
                  <div className="bg-muted/50 border border-border rounded-xl p-4">
                    <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2">
                      Total — {filteredHistory.length} payment{filteredHistory.length !== 1 ? 's' : ''}
                    </p>
                    <div className="grid grid-cols-3 gap-2 text-xs">
                      <div className="text-center">
                        <p className="text-muted-foreground mb-0.5">Amount</p>
                        <p className="font-bold">Rs.{filteredHistory.reduce((s: number, t: any) => s + (Number(t.subtotal) || Math.max(0, (Number(t.total) || 0) - (Number(t.serviceFee) || 0))), 0).toFixed(2)}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-muted-foreground mb-0.5">Fees</p>
                        <p className="font-bold text-primary">Rs.{filteredHistory.reduce((s: number, t: any) => s + (Number(t.serviceFee) || 0), 0).toFixed(2)}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-muted-foreground mb-0.5">Total</p>
                        <p className="font-bold">Rs.{filteredHistory.reduce((s: number, t: any) => s + (t.total || 0), 0).toFixed(2)}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Desktop table view */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border text-left">
                        <th className="pb-2 font-semibold text-muted-foreground">Invoice #</th>
                        <th className="pb-2 font-semibold text-muted-foreground">Type</th>
                        <th className="pb-2 font-semibold text-muted-foreground">Customer</th>
                        <th className="pb-2 font-semibold text-muted-foreground text-right">Amount</th>
                        <th className="pb-2 font-semibold text-muted-foreground text-right">Fee</th>
                        <th className="pb-2 font-semibold text-muted-foreground text-right">Total</th>
                        <th className="pb-2 font-semibold text-muted-foreground">Payment</th>
                        <th className="pb-2 font-semibold text-muted-foreground">Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredHistory.map((t: any) => (
                        <tr key={t.id} className="border-b border-border/50 hover:bg-muted/30">
                          <td className="py-2.5 font-mono text-xs">{t.transactionNumber || t.receiptNumber}</td>
                          <td className="py-2.5">{t.billType || '-'}</td>
                          <td className="py-2.5">{t.customerName || '-'}</td>
                          <td className="py-2.5 text-right">Rs.{(Number(t.subtotal) || Math.max(0, (Number(t.total) || 0) - (Number(t.serviceFee) || 0))).toFixed(2)}</td>
                          <td className="py-2.5 text-right text-primary">{t.serviceFee ? `Rs.${Number(t.serviceFee).toFixed(2)}` : '-'}</td>
                          <td className="py-2.5 text-right font-bold">Rs.{(Number(t.total) || 0).toFixed(2)}</td>
                          <td className="py-2.5 capitalize">{(t.paymentMethod || 'cash').replace(/_/g, ' ')}</td>
                          <td className="py-2.5 text-xs text-muted-foreground">{new Date(t.createdAt).toLocaleTimeString()}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2 border-border font-bold">
                        <td colSpan={3} className="py-2.5">Total ({filteredHistory.length} payments)</td>
                        <td className="py-2.5 text-right">Rs.{filteredHistory.reduce((s: number, t: any) => s + (Number(t.subtotal) || Math.max(0, (Number(t.total) || 0) - (Number(t.serviceFee) || 0))), 0).toFixed(2)}</td>
                        <td className="py-2.5 text-right text-primary">Rs.{filteredHistory.reduce((s: number, t: any) => s + (Number(t.serviceFee) || 0), 0).toFixed(2)}</td>
                        <td className="py-2.5 text-right">Rs.{filteredHistory.reduce((s: number, t: any) => s + (t.total || 0), 0).toFixed(2)}</td>
                        <td colSpan={2}></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </>
            )}
          </div>
        )}

        {showReceipt && lastTransaction && (
          <BillPaymentReceipt
            transaction={lastTransaction}
            storeName={settings.storeName}
            storeAddress={settings.storeAddress}
            storePhone={settings.storePhone}
            storeEmail={settings.storeEmail}
            receiptFooter={settings.receiptFooter}
            onClose={() => { setShowReceipt(false); resetForm() }}
            onNewPayment={() => { setShowReceipt(false); resetForm() }}
          />
        )}
      </div>
    </div>
  )
}
